import { useEffect, useState } from "react";
import axios from "axios";
import { Activity, Wifi, Globe, Shield, Zap, ChevronRight, X, Server, Radio } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const BandwidthWidget = () => {
  const [bandwidth, setBandwidth] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const fetchBandwidth = () => {
      axios.get(`${API}/proxy/bandwidth`).then(r => setBandwidth(r.data)).catch(() => {});
    };
    fetchBandwidth();
    const interval = setInterval(fetchBandwidth, 60000);
    return () => clearInterval(interval);
  }, []);

  if (!bandwidth) return null;

  const pct = bandwidth.usage_percent || 0;
  const color = pct > 80 ? "#EF4444" : pct > 50 ? "#F59E0B" : "#32B4AC";
  const usedDisplay = bandwidth.bandwidth_used_gb < 0.01
    ? (bandwidth.bandwidth_used_bytes / (1024 * 1024)).toFixed(1) + " MB"
    : bandwidth.bandwidth_used_gb.toFixed(2) + " GB";

  return (
    <div className="fixed bottom-16 left-6 z-40" data-testid="bandwidth-floating-widget">
      {!expanded && (
        <button
          onClick={() => setExpanded(true)}
          className="group relative w-12 h-12 rounded-full bg-white border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center"
          title="Proxy Network Status"
          data-testid="bandwidth-toggle"
        >
          <svg className="w-10 h-10 -rotate-90" viewBox="0 0 40 40">
            <circle cx="20" cy="20" r="16" fill="none" stroke="#F3F4F6" strokeWidth="2.5" />
            <circle cx="20" cy="20" r="16" fill="none"
              stroke={color} strokeWidth="2.5" strokeLinecap="round"
              strokeDasharray={`${pct * 1.005} 100.5`}
              className="transition-all duration-1000"
            />
          </svg>
          <span className="absolute inset-0 flex items-center justify-center text-[8px] font-bold text-gray-700">
            {Math.round(pct)}%
          </span>
          {pct > 80 && (
            <span className="absolute inset-0 rounded-full border-2 border-red-400 animate-ping opacity-30" />
          )}
          {/* Live indicator dot */}
          <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-green-500 border-2 border-white">
            <span className="absolute inset-0 rounded-full bg-green-400 animate-ping opacity-60" />
          </span>
        </button>
      )}
      {expanded && (
        <div className="bg-gray-950 rounded-2xl border border-gray-800 shadow-2xl w-72 overflow-hidden"
          data-testid="bandwidth-expanded"
          style={{ boxShadow: "0 0 40px rgba(50, 180, 172, 0.15)" }}
        >
          {/* Header */}
          <div className="px-4 pt-4 pb-3 border-b border-gray-800/60">
            <div className="flex items-center justify-between mb-0.5">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-lg bg-[#32B4AC]/20 flex items-center justify-center">
                  <Radio className="w-3.5 h-3.5 text-[#32B4AC]" />
                </div>
                <span className="text-xs font-bold text-white tracking-wide">PROXY NETWORK</span>
              </div>
              <button onClick={() => setExpanded(false)}
                className="text-gray-500 hover:text-gray-300 transition-colors p-1 rounded-lg hover:bg-gray-800"
                data-testid="bandwidth-collapse">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="flex items-center gap-1.5 mt-1">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
              <span className="text-[10px] text-green-400 font-medium">Connected &middot; All systems operational</span>
            </div>
          </div>

          {/* Bandwidth Gauge */}
          <div className="px-4 py-3">
            <div className="flex items-center gap-3">
              <div className="relative w-14 h-14 shrink-0">
                <div className="absolute inset-[-3px] rounded-full border border-dashed border-gray-700 animate-[spin_12s_linear_infinite]" />
                <svg className="w-14 h-14 -rotate-90" viewBox="0 0 56 56">
                  <circle cx="28" cy="28" r="22" fill="none" stroke="#1F2937" strokeWidth="3" />
                  <circle cx="28" cy="28" r="22" fill="none"
                    stroke={color} strokeWidth="3" strokeLinecap="round"
                    strokeDasharray={`${pct * 1.382} 138.2`}
                    className="transition-all duration-1000"
                  />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-white">
                  {Math.round(pct)}%
                </span>
              </div>
              <div>
                <div className="text-lg font-bold text-white tabular-nums">{usedDisplay}</div>
                <div className="text-[10px] text-gray-500">of {bandwidth.bandwidth_limit_gb} GB limit</div>
                <div className="w-28 h-1 bg-gray-800 rounded-full overflow-hidden mt-1.5">
                  <div className="h-full rounded-full transition-all duration-1000"
                    style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: color }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Request Stats */}
          <div className="px-4 pb-3">
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-gray-900 rounded-lg p-2 text-center">
                <div className="text-sm font-bold text-white tabular-nums">{(bandwidth.requests_total || 0).toLocaleString()}</div>
                <div className="text-[9px] text-gray-500 uppercase tracking-wider">Requests</div>
              </div>
              <div className="bg-gray-900 rounded-lg p-2 text-center">
                <div className="text-sm font-bold text-green-400 tabular-nums">{bandwidth.success_rate || 0}%</div>
                <div className="text-[9px] text-gray-500 uppercase tracking-wider">Success</div>
              </div>
              <div className="bg-gray-900 rounded-lg p-2 text-center">
                <div className="text-sm font-bold text-red-400 tabular-nums">{(bandwidth.requests_failed || 0).toLocaleString()}</div>
                <div className="text-[9px] text-gray-500 uppercase tracking-wider">Failed</div>
              </div>
            </div>
          </div>

          {/* Proxy Tech Details */}
          <div className="px-4 pb-4">
            <div className="bg-gray-900/80 rounded-xl border border-gray-800 divide-y divide-gray-800/60">
              {[
                { icon: Shield, label: "Provider", value: bandwidth.proxy_provider || "Webshare", color: "text-[#32B4AC]" },
                { icon: Server, label: "Type", value: bandwidth.proxy_type || "Residential", color: "text-blue-400" },
                { icon: Globe, label: "Geo-Target", value: bandwidth.proxy_geo || "India (IN)", color: "text-amber-400" },
                { icon: Zap, label: "Rotation", value: bandwidth.proxy_rotation || "Auto-Rotate", color: "text-purple-400" },
                { icon: Wifi, label: "Protocol", value: bandwidth.proxy_protocol || "HTTP/HTTPS", color: "text-cyan-400" },
                { icon: Activity, label: "Pool Size", value: bandwidth.proxy_pool || "10M+ IPs", color: "text-emerald-400" },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 px-3 py-2">
                  <item.icon className={`w-3.5 h-3.5 ${item.color} shrink-0`} />
                  <span className="text-[10px] text-gray-500 w-16 shrink-0">{item.label}</span>
                  <span className="text-[11px] text-gray-300 font-medium">{item.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
          {bandwidth.throttled && (
            <div className="px-4 pb-3">
              <div className="bg-red-900/30 border border-red-800/40 rounded-lg px-3 py-2 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                <span className="text-[10px] text-red-400 font-medium">Bandwidth throttled — upgrade plan</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BandwidthWidget;
